import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Chart from "react-apexcharts";
import {
  MdBusiness,
  MdPeople,
  MdAttachMoney,
  MdSearch,
  MdTrendingUp,
  MdTrendingDown,
  MdCheckCircle,
  MdError,
  MdInfo,
  MdLocationOn,
  MdCurrencyYen,
  MdBlock,
  MdOutlineRealEstateAgent,

} from "react-icons/md";
import { IoLanguage } from "react-icons/io5";
import { FaUserFriends } from "react-icons/fa";

import {
  FaDollarSign,
  FaYenSign,
  FaCoins,
  FaMoneyBillWave,
} from "react-icons/fa";
import { FaBuilding } from "react-icons/fa";
import { fetchAdminDashboard } from "../redux/actions/dashboard/dashboardActions";
import { getOemNames, getScanTypes } from "../redux/actions/oemActions";
import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  Badge,
  Button,
} from "react-bootstrap";
import "../styles/EnhancedDashboard.css";

const Dashboard = () => {
  const dispatch = useDispatch();
  const {
    data: dashboardData,
    loading,
    error,
  } = useSelector((state) => state.dashboard);
  const { theme } = useSelector((state) => state.theme);
  const [oemFilter, setOemFilter] = useState(null);
  const [scanTypeFilter, setScanTypeFilter] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [notifications, setNotifications] = useState(7);
  const [threatLevel, setThreatLevel] = useState("Medium");
  const [realTimeData, setRealTimeData] = useState([
    45, 67, 23, 89, 34, 78, 56,
  ]);
  const [isScanning, setIsScanning] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const rates = dashboardData?.response?.conversionRates;
  const [lineChartData, setLineChartData] = useState(
    Array.from({ length: 24 }, (_, i) => Math.floor(Math.random() * 100))
  );
  const [areaChartData, setAreaChartData] = useState(
    Array.from({ length: 12 }, (_, i) => Math.floor(Math.random() * 80) + 20)
  );
  const [trafficData, setTrafficData] = useState({
    inbound: Array.from(
      { length: 10 },
      () => Math.floor(Math.random() * 50) + 10
    ),
    outbound: Array.from(
      { length: 10 },
      () => Math.floor(Math.random() * 40) + 5
    ),
  });
  // filter
  const { oemNames, scanTypes } = useSelector((state) => state.oem);
  useEffect(() => {
    dispatch(getOemNames());
    dispatch(getScanTypes());
  }, [dispatch]);
  // Check if filtered data has no results
  const hasNoData =
    oemFilter &&
    dashboardData?.response?.metrics &&
    dashboardData.response.metrics.oemPartnerCount === 0 &&
    dashboardData.response.metrics.oemRelatedUserCount === 0 &&
    dashboardData.response.metrics.scanCount === 0;

  // Use API data for security stats with fallback
  const securityStats = dashboardData?.response?.metrics
    ? [
        {
          title: "OEM Partners",
          value: dashboardData.response.metrics.oemPartnerCount.toString(),
          trend: "up",
          icon: MdBusiness,
          color: "#10b981",
          status: "success",
        },
        {
          title: "OEM Users",
          value: dashboardData.response.metrics.oemRelatedUserCount.toString(),
          trend: "up",
          icon: MdPeople,
          color: "#3b82f6",
          status: "info",
        },
        {
          title: "Total Scans",
          value: dashboardData.response.metrics.scanCount.toString(),
          trend: "up",
          icon: MdSearch,
          color: "#f59e0b",
          status: "warning",
        },
        {
          title: "Revenue (USD)",
          value: `$${(
            dashboardData.response.metrics.revenue.usd || 0
          ).toLocaleString()}`,
          trend: "up",
          icon: FaDollarSign,
          color: "#28a745",
          status: "critical",
        },
        {
          title: "Revenue (JPY)",
          value: `¥${(
            dashboardData.response.metrics.revenue.jpy || 0
          ).toLocaleString()}`,
          trend: "up",
          icon: FaYenSign,
          color: "#6f42c1",
          status: "critical",
        },
        {
          title: "Revenue (SGD)",
          value: `S$${(
            dashboardData.response.metrics.revenue.sgd || 0
          ).toLocaleString()}`,
          trend: "up",
          icon: FaCoins,
          color: "#fd7e14",
          status: "critical",
        },
        {
          title: "Revenue (HKD)",
          value: `HK$${(
            dashboardData.response.metrics.revenue.hkd || 0
          ).toLocaleString()}`,
          trend: "up",
          icon: FaMoneyBillWave,
          color: "#20c997",
          status: "critical",
        },
        {
          title: "Revenue (AUD)",
          value: `A$${(
            dashboardData.response.metrics.revenue.aud || 0
          ).toLocaleString()}`,
          trend: "up",
          icon: FaDollarSign,
          color: "#17a2b8",
          status: "critical",
        },
      ]
    : [
        {
          title: "OEM Partners",
          value: "0",
          trend: "up",
          icon: MdBusiness,
          color: "#10b981",
          status: "success",
        },
        {
          title: "OEM Users",
          value: "0",
          trend: "up",
          icon: MdPeople,
          color: "#3b82f6",
          status: "info",
        },
        {
          title: "Total Scans",
          value: "0",
          trend: "up",
          icon: MdSearch,
          color: "#f59e0b",
          status: "warning",
        },
        {
          title: "Revenue (USD)",
          value: "$0",
          trend: "up",
          icon: MdAttachMoney,
          color: "#ef4444",
          status: "critical",
        },
      ];

  // Use API data for plan distribution
  const pieChartData = dashboardData?.response?.metrics?.planDistribution
    ? [
        {
          label: "Per Report",
          value:
            dashboardData.response.metrics.planDistribution.monthly.percentage,
          count: dashboardData.response.metrics.planDistribution.monthly.count,
          color: "#3b82f6",
        },
        {
          label: "Annual Plans",
          value:
            dashboardData.response.metrics.planDistribution.annual.percentage,
          count: dashboardData.response.metrics.planDistribution.annual.count,
          color: "#10b981",
        },
      ].filter((item) => item.value > 0)
    : [];

  // Use API data for language distribution
  const languageData = dashboardData?.response?.languageDistribution
    ? [
        {
          label: "English",
          value: dashboardData.response.languageDistribution.eng,
          color: "#3b82f6",
          flag: "🇺🇸",
          code: "EN",
        },
        {
          label: "Japanese",
          value: dashboardData.response.languageDistribution.ja,
          color: "#10b981",
          flag: "🇯🇵",
          code: "JA",
        },
      ].filter((item) => item.value > 0)
    : [];

  const totalLanguageUsers = languageData.reduce(
    (sum, lang) => sum + lang.value,
    0
  );

  const hasValidPieData =
    pieChartData.length > 0 && pieChartData.some((item) => item.value > 0);

  // Use API data for country locations
  const countryCoordinates = {
    India: { lat: 20.5937, lng: 78.9629 },
    "United States": { lat: 39.8283, lng: -98.5795 },
    "United Kingdom": { lat: 55.3781, lng: -3.436 },
    Japan: { lat: 36.2048, lng: 138.2529 },
    Australia: { lat: -25.2744, lng: 133.7751 },
    Comoros: { lat: -11.6455, lng: 43.3333 },
    "Falkland Islands": { lat: -51.7963, lng: -59.5236 },
    Sudan: { lat: 12.8628, lng: 30.2176 },
    Zimbabwe: { lat: -19.0154, lng: 29.1549 },
    "Trinidad and Tobago": { lat: 10.6918, lng: -61.2225 },
  };

  const trafficLocations =
    dashboardData?.response?.usersByOemPartnerCountry?.map((location) => ({
      country: location.country,
      users: location.users,
      oems: location.oems,
      total: location.total,
      lat: countryCoordinates[location.country]?.lat || 0,
      lng: countryCoordinates[location.country]?.lng || 0,
    })) || [];

  useEffect(() => {
    // Fetch dashboard data on component mount
    const oemId = oemFilter?.value || "";
    const scanType = scanTypeFilter?.value?.toLowerCase() || "";
    dispatch(fetchAdminDashboard(oemId, scanType));
  }, [dispatch, oemFilter, scanTypeFilter]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRealTimeData((prev) =>
        prev.map(() => Math.floor(Math.random() * 100))
      );
      setNotifications((prev) =>
        Math.max(0, prev + Math.floor(Math.random() * 3) - 1)
      );
      setLineChartData((prev) => [
        ...prev.slice(1),
        Math.floor(Math.random() * 100),
      ]);
      setAreaChartData((prev) => [
        ...prev.slice(1),
        Math.floor(Math.random() * 80) + 20,
      ]);
      setTrafficData((prev) => ({
        inbound: [
          ...prev.inbound.slice(1),
          Math.floor(Math.random() * 50) + 10,
        ],
        outbound: [
          ...prev.outbound.slice(1),
          Math.floor(Math.random() * 40) + 5,
        ],
      }));
      // Update threat levels
      trafficLocations.forEach((location) => {
        const change = Math.floor(Math.random() * 6) - 3;
        location.threats = Math.max(5, Math.min(70, location.threats + change));
      });
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let scanInterval;
    if (isScanning) {
      scanInterval = setInterval(() => {
        console.log("Scanning for threats...");
      }, 2000);
    }
    return () => clearInterval(scanInterval);
  }, [isScanning]);

  const updateThreatData = () => {
    const levels = ["Low", "Medium", "High", "Critical"];
    setThreatLevel(levels[Math.floor(Math.random() * levels.length)]);
  };

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case "critical":
        return "#ef4444";
      case "high":
        return "#f59e0b";
      case "medium":
        return "#3b82f6";
      case "low":
        return "#10b981";
      default:
        return "#6b7280";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <MdError style={{ color: "#ef4444" }} />;
      case "blocked":
        return <MdBlock style={{ color: "#6b7280" }} />;
      case "mitigated":
        return <MdCheckCircle style={{ color: "#10b981" }} />;
      case "investigating":
        return <MdInfo style={{ color: "#3b82f6" }} />;
      default:
        return <MdInfo />;
    }
  };

  // Use API data for recent scans
  const recentScans = dashboardData?.response?.recentScans || [];
  const filteredScans = recentScans.filter((scan) => {
    const matchesSearch =
      scan.domain.toLowerCase().includes(searchTerm.toLowerCase()) ||
      scan.user.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Use API data for recent transactions
  const recentTransactions = dashboardData?.response?.recentTransactions || [];

  // Use API data for top revenue OEM partners
  const topRevenuePartners =
    dashboardData?.response?.topRevenueOemPartners || [];

  // Use API data for top domains
  const topDomains = dashboardData?.response?.topDomains || [];

  // Show loading state
  if (loading) {
    return (
      <div className="page-container">
        <div className="page-card scroll-container">
          <Container
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "100vh",
              flexDirection: "column",
            }}
          >
            <div className="spinner-enhanced mb-3"></div>
            <p style={{ color: "#7f8c8d", fontSize: "14px" }}>
              Loading Dashboard...
            </p>
          </Container>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="page-container">
        <div className="page-card scroll-container">
          <Container>
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "400px" }}
            >
              <div className="text-center loading-enhanced">
                <MdError size={48} className="text-danger mb-3" />
                <p className="text-muted mb-3 fw-medium">
                  Error loading dashboard: {error}
                </p>
                <Button
                  variant="primary"
                  className="badge-enhanced bg-primary"
                  onClick={() =>
                    dispatch(
                      fetchAdminDashboard(
                        oemFilter?.value || "",
                        scanTypeFilter?.value?.toLowerCase() || ""
                      )
                    )
                  }
                >
                  Retry
                </Button>
              </div>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-card scroll-container">
        <div className="dashboard-enhanced">
          <Container fluid className="py-2 px-3">
            {/* Header with Filter */}
            <Row className="mb-3 align-items-center">
              <Col md={7}>
                <h4 className="fw-bold text-dark mb-0" style={{fontSize:"16px"}}>Dashboard Overview</h4>
                <p className="text-muted mb-0 small">
                  Monitor your OEM partners and scan analytics
                </p>
              </Col>
              <Col md={5}>
                <div className="d-flex align-items-center gap-3 justify-content-end">
                  <div
                    style={{
                      minWidth: "200px",
                      position: "relative",
                    }}
                  >
                    <label className="form-label fw-bold small mb-0" style={{fontSize:"12px"}}>
                      Filters by OEM
                    </label>
                    <Select
                      value={oemFilter}
                      onChange={setOemFilter}
                      options={oemNames?.map((oem) => ({
                        value: oem._id,
                        label: oem.companyName,
                      }))}
                      placeholder="Filter OEM Partners"
                      isClearable
                      isSearchable
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: "36px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: "white",
                          boxShadow: state.isFocused
                            ? "0 0 0 2px rgba(59, 130, 246, 0.1)"
                            : "none",
                          "&:hover": { border: "1px solid #9ca3af" },
                          transition: "all 0.2s ease",
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                          backgroundColor: "white",
                          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "#9ca3af",
                          fontSize: "14px",
                        }),
                      }}
                    />
                  </div>
                  <div
                    style={{
                      minWidth: "200px",
                      position: "relative",
                    }}
                  >
                    <label className="form-label fw-bold small mb-0"  style={{fontSize:"12px"}}>
                      Filters by Scan Type
                    </label>
                    <Select
                      value={scanTypeFilter}
                      onChange={setScanTypeFilter}
                      options={scanTypes?.map((scanType) => ({
                        value: scanType.name,
                        label: scanType.name,
                      }))}
                      placeholder="Filter Scan Types"
                      isClearable
                      isSearchable
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: "36px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          backgroundColor: "white",
                          boxShadow: state.isFocused
                            ? "0 0 0 2px rgba(16, 185, 129, 0.1)"
                            : "none",
                          "&:hover": { border: "1px solid #9ca3af" },
                          transition: "all 0.2s ease",
                        }),
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                          backgroundColor: "white",
                          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "#9ca3af",
                          fontSize: "14px",
                        }),
                      }}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            {/* Stats Cards */}
            <Row className="mb-2">
              {securityStats.slice(0, 4).map((stat, index) => (
                <Col key={index} md={3} className="mb-2">
                  <Card
                    className="h-100 stats-card-enhanced card-enhanced fade-in"
                    style={{
                      "--card-accent-color":
                        "linear-gradient(135deg, #1e40af, #3b82f6)",
                      animationDelay: `${index * 0.1}s`,
                    }}
                  >
                    <Card.Body className="d-flex align-items-center p-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-2 stats-icon-enhanced"
                        style={{
                          width: "40px",
                          height: "40px",
                          backgroundColor: `${stat.color}15`,
                          color: stat.color,
                          background: `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}25 100%)`,
                        }}
                      >
                        <stat.icon size={22} />
                      </div>
                      <div className="flex-grow-1">
                        <h6
                          className="mb-0 fw-bold"
                          style={{ fontSize: "18px" }}
                        >
                          {stat.value}
                        </h6>
                        <p
                          className="text-muted mb-0 small fw-medium"
                          style={{ fontSize: "13px" }}
                        >
                          {stat.title}
                        </p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            {/* Revenue Cards */}
            {securityStats.length > 4 && (
              <Row className="mb-2">
                {securityStats.slice(4).map((stat, index) => (
                  <Col key={index + 4} md={3} className="mb-2">
                    <Card
                      className="h-100 stats-card-enhanced card-enhanced fade-in"
                      style={{
                        "--card-accent-color":
                          "linear-gradient(135deg, #1e40af, #3b82f6)",
                        animationDelay: `${(index + 4) * 0.1}s`,
                      }}
                    >
                      <Card.Body className="d-flex align-items-center p-3">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-2 stats-icon-enhanced"
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: `${stat.color}15`,
                            color: stat.color,
                            background: `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}25 100%)`,
                          }}
                        >
                          <stat.icon size={22} />
                        </div>
                        <div className="flex-grow-1">
                          <h6
                            className="mb-0 fw-bold"
                            style={{ fontSize: "18px" }}
                          >
                            {stat.value}
                          </h6>
                          <p
                            className="text-muted mb-0 small fw-medium"
                            style={{ fontSize: "13px" }}
                          >
                            {stat.title}
                          </p>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}

            {/* Charts Row */}
            <Row className="mb-2">
              {/* Partner Distribution */}
              {dashboardData?.response?.partnerCounts && (
                <Col
                  md={!dashboardData?.response?.oemDetails ? 4 : 4}
                  className="mb-3"
                >
                  <Card className="h-100 chart-card-enhanced card-enhanced fade-in">
                    <Card.Header className="chart-header-enhanced">
                      <h6 className="mb-0 fw-bold"><span className="me-2"><FaUserFriends/></span> Partner Distribution</h6>
                    </Card.Header>
                    <Card.Body className="py-4">
                      <Row>
                        <Col md={12} className="text-center mb-3">
                          <div className="p-3 bg-success bg-opacity-10 rounded">
                            <h4 className="fw-bold text-primary mb-1">
                              {
                                dashboardData.response.partnerCounts
                                  .almataPartners
                              }
                            </h4>
                            <p className="mb-0 small fw-medium">
                              Almata Partners
                            </p>
                          </div>
                        </Col>
                        <Col md={12} className="text-center mb-3">
                          <div className="p-3 bg-success bg-opacity-10 rounded">
                            <h4 className="fw-bold text-success mb-1">
                              {
                                dashboardData.response.partnerCounts
                                  .itSalesPartners
                              }
                            </h4>
                            <p className="mb-0 small fw-medium">
                              IT Sales Partners
                            </p>
                          </div>
                        </Col>
                        <Col md={12} className="text-center mb-3">
                          <div className="p-3 bg-warning bg-opacity-10 rounded">
                            <h4 className="fw-bold text-warning mb-1">
                              {
                                dashboardData.response.partnerCounts.oemPartners
                                  .total
                              }
                            </h4>
                            <p className="mb-0 small fw-medium">OEM Partners</p>
                          </div>
                        </Col>
                      </Row>
                      <div className="mt-3">
                        <h6 className="fw-semibold mb-2">
                          OEM Partners by Creator:
                        </h6>
                        <div className="d-flex justify-content-between">
                          <div className="text-center fs-6">
                            <div className="fw-bold text-info">
                              {
                                dashboardData.response.partnerCounts.oemPartners
                                  .byCreator.admin
                              }
                            </div>
                            <small className="text-muted">Admin</small>
                          </div>
                          <div className="text-center fs-6">
                            <div className="fw-bold text-primary">
                              {
                                dashboardData.response.partnerCounts.oemPartners
                                  .byCreator.cyberriskreport
                              }
                            </div>
                            <small className="text-muted">Almata</small>
                          </div>
                          <div className="text-center fs-6">
                            <div className="fw-bold text-success">
                              {
                                dashboardData.response.partnerCounts.oemPartners
                                  .byCreator.itSales
                              }
                            </div>
                            <small className="text-muted">IT Sales</small>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Plan Distribution */}
              <Col
                md={!dashboardData?.response?.oemDetails ? 4 : 4}
                className="mb-3"
              >
                <Card className="h-100 chart-card-enhanced card-enhanced fade-in">
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-bold"><span className="me-2"><MdOutlineRealEstateAgent /></span> Plan Distribution</h6>
                  </Card.Header>
                  <Card.Body className="py-4">
                    {hasValidPieData ? (
                      <div>
                        <Chart
                          options={{
                            chart: {
                              type: "donut",
                              animations: {
                                enabled: true,
                                easing: "easeinout",
                                speed: 800,
                              },
                            },
                            labels: pieChartData.map((item) => item.label),
                            colors: ["#06b6d4", "#8b5cf6"],
                            legend: {
                              position: "bottom",
                              fontSize: "12px",
                              fontWeight: 500,
                            },
                            plotOptions: {
                              pie: {
                                donut: {
                                  size: "70%",
                                  labels: {
                                    show: true,
                                    total: {
                                      show: true,
                                      label: "Total",
                                      fontSize: "16px",
                                      fontWeight: 600,
                                      color: "#374151",
                                      formatter: function (w) {
                                        return pieChartData.reduce(
                                          (sum, item) => sum + item.count,
                                          0
                                        );
                                      },
                                    },
                                  },
                                },
                              },
                            },
                            dataLabels: {
                              enabled: false,
                            },
                            stroke: {
                              show: false,
                            },
                          }}
                          series={pieChartData.map((item) => item.value)}
                          type="donut"
                          height={250}
                        />
                        <div className="mt-3">
                          {pieChartData.map((item, index) => (
                            <div
                              key={index}
                              className="d-flex justify-content-between align-items-center mb-2"
                            >
                              <div className="d-flex align-items-center">
                                <div
                                  className="me-2"
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor:
                                      index === 0 ? "#06b6d4" : "#8b5cf6",
                                    borderRadius: "2px",
                                  }}
                                ></div>
                                <span className="small fs-6">{item.label}</span>
                              </div>
                              <div className="text-end fs-6">
                                <div className="fw-bold">{item.count}</div>
                                <small className="text-success">
                                  +{item.value.toFixed(1)}%
                                </small>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <p className="mb-0">No plan data available</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Language Distribution */}
              <Col
                md={!dashboardData?.response?.oemDetails ? 4 : 4}
                className="mb-3"
              >
                <Card className="h-100 chart-card-enhanced card-enhanced fade-in">
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-bold"><span className="me-2"><IoLanguage /></span> Language Distribution</h6>
                  </Card.Header>
                  <Card.Body className="py-4">
                    {languageData.length > 0 ? (
                      <Chart
                        options={{
                          chart: {
                            type: "bar",
                            toolbar: { show: false },
                          },
                          plotOptions: {
                            bar: {
                              horizontal: false,
                              columnWidth: "30%",
                              borderRadius: 8,
                              borderRadiusApplication: "around",
                            },
                          },
                          colors: ["#3b82f6", "#10b981"],
                          xaxis: {
                            categories: languageData.map((item) => item.code),
                            labels: {
                              style: {
                                fontSize: "12px",
                                fontWeight: 500,
                              },
                            },
                          },
                          yaxis: {
                            labels: {
                              style: {
                                fontSize: "12px",
                              },
                            },
                          },
                          dataLabels: {
                            enabled: false,
                          },
                          grid: {
                            show: true,
                            borderColor: "#f1f5f9",
                          },
                          legend: {
                            show: false,
                          },
                        }}
                        series={[
                          {
                            name: "Users",
                            data: languageData.map((item) => item.value),
                          },
                        ]}
                        type="bar"
                        height={350}
                      />
                    ) : (
                      <div className="text-center text-muted py-4">
                        <p className="mb-0">No language data available</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            {/* Main Content Row */}
            <Row className="mb-2">
              <Col lg={8}>
                <Row>
                  <Col lg={12}>
                    {/* Scan Analytics */}
                    <Card className="mb-4 chart-card-enhanced card-enhanced fade-in">
                      <Card.Header className="chart-header-enhanced">
                        <h6 className="mb-0 fw-semibold text-gradient">
                          <MdTrendingUp className="me-2" /> Scan Analytics
                        </h6>
                      </Card.Header>
                      <Card.Body>
                        {dashboardData?.response?.scanAnalytics ? (
                          <Row>
                            {[
                              {
                                label: "Total Scans",
                                value:
                                  dashboardData.response.scanAnalytics.total,
                                color: "primary",
                              },
                              {
                                label: "Completed",
                                value:
                                  dashboardData.response.scanAnalytics
                                    .completed,
                                color: "success",
                              },
                              {
                                label: "Pending",
                                value:
                                  dashboardData.response.scanAnalytics.pending,
                                color: "warning",
                                customColor: "#ffc107",
                              },
                              {
                                label: "Failed",
                                value:
                                  dashboardData.response.scanAnalytics.failed,
                                color: "danger",
                              },
                              {
                                label: "Success Rate",
                                value: `${dashboardData.response.scanAnalytics.completionRate}%`,
                                color: "info",
                              },
                            ].map((item, index) => (
                              <Col key={index} className="text-center mb-3">
                                <div
                                  className={
                                    item.customColor
                                      ? "fw-bold fs-4"
                                      : `text-${item.color} fw-bold fs-4`
                                  }
                                  style={
                                    item.customColor
                                      ? { color: item.customColor }
                                      : {}
                                  }
                                >
                                  {item.value}
                                </div>
                                <div className="text-muted small">
                                  {item.label}
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <div className="text-center text-muted py-4">
                            <p className="mb-0">
                              {hasNoData
                                ? "No scan analytics for selected OEM"
                                : "No scan analytics available"}
                            </p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={dashboardData?.response?.oemDetails ? 6 : 12}>
                    {/* Global User Distribution */}
                    <Card
                      className="mb-4 chart-card-enhanced card-enhanced fade-in"
                      style={{ animationDelay: "0.1s" }}
                    >
                      <Card.Header className="chart-header-enhanced">
                        <h6 className="mb-0 fw-semibold text-gradient">
                          <MdLocationOn className="me-2" /> Global User Distribution
                        </h6>
                      </Card.Header>
                      <Card.Body style={{ height: "260px", padding: "15px", overflowY: "auto" }}>
                        {trafficLocations.length > 0 ? (
                          <Row>
                            {trafficLocations.map((location, index) => (
                              <Col key={index} md={6} lg={dashboardData?.response?.oemDetails ? 6 : 4} className="mb-1 p-1">
                                <div className="p-3 h-100" style={{backgroundColor: "#f8f9fa", borderRadius: "4px", border: "1px solid #e9ecef", transition: "none", boxShadow: "none"}}>
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 fw-semibold">
                                      {location.country}
                                    </h6>
                                    <MdLocationOn
                                      className="text-primary"
                                      size={20}
                                    />
                                  </div>
                                  <div className="small text-muted d-flex justify-content-between align-items-center">
                                    <div className="fs-6">
                                      Users:{" "}
                                      <span className="fw-bold text-primary">
                                        {location.users}
                                      </span>
                                    </div>
                                    <div className="fs-6">
                                      OEMs:{" "}
                                      <span className="fw-bold text-success">
                                        {location.oems}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            ))}
                          </Row>
                        ) : (
                          <div className="text-center text-muted py-4">
                            <p className="mb-0">
                              No global distribution data available
                            </p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  {dashboardData?.response?.oemDetails && (
                    <Col lg={6}>
                      <Card
                        className="mb-4 chart-card-enhanced card-enhanced fade-in"
                        style={{ animationDelay: "0.1s" }}
                      >
                        <Card.Header className="chart-header-enhanced">
                          <h6 className="mb-0 fw-semibold text-gradient">
                            <MdBusiness className="me-2" /> OEM Details
                          </h6>
                        </Card.Header>
                        <Card.Body style={{ height: "260px", padding: "15px", overflowY: "auto" }}>
                          <div className="text-center mb-2">
                            <div className="position-relative d-inline-block">
                              <div style={{width: "120px", height: "120px", borderRadius: "50%", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto", position: "relative"}}>
                                <div style={{width: "80px", height: "80px", borderRadius: "50%", backgroundColor: "white", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                  <MdBusiness size={32} style={{color: "#667eea"}} />
                                </div>
                              </div>
                              <div className="position-absolute" style={{top: "10px", right: "10px", width: "24px", height: "24px", borderRadius: "50%", backgroundColor: dashboardData.response.oemDetails.scanType === 'Multiple' ? '#10b981' : '#3b82f6', border: "3px solid white"}}></div>
                            </div>
                            <h5 className="fw-bold mt-3 mb-1">{dashboardData.response.oemDetails.companyName}</h5>
                            <p className="text-muted small mb-0">{dashboardData.response.oemDetails.email}</p>
                          </div>
                          <div className="row g-2 mb-0">
                            <div className="col-6">
                              <div className="px-2 py-1 mb-0 rounded d-flex justify-content-between align-items-center" style={{backgroundColor: "#f8fafc", border: "1px solid #e2e8f0"}}>
                                <div className="small text-muted">Scan Type</div>
                                <div className="fw-bold" style={{color: dashboardData.response.oemDetails.scanType === 'Multiple' ? '#10b981' : '#3b82f6'}}>{dashboardData.response.oemDetails.scanType}</div>
                              </div>
                            </div>
                            <div className="col-6">
                              <div className="px-2 py-1 mb-0 rounded d-flex justify-content-between align-items-center" style={{backgroundColor: "#f8fafc", border: "1px solid #e2e8f0"}}>
                                <div className="small text-muted">Plan</div>
                                <div className="fw-bold" style={{color: dashboardData.response.oemDetails.plan.type === 'payment_plans' ? '#f59e0b' : '#6b7280'}}>{dashboardData.response.oemDetails.plan.type === 'payment_plans' ? 'Paid' : 'Free'}</div>
                              </div>
                            </div>
                          </div>
                          {dashboardData.response.oemDetails.plan.type === 'payment_plans' && Array.isArray(dashboardData.response.oemDetails.plan.data) && (
                            <div className="mt-1">
                              <div className="small text-muted mb-2 fw-bold">Payment Plans:</div>
                              <div className="row g-2">
                                {dashboardData.response.oemDetails.plan.data.map((plan, index) => (
                                  <div key={index} className="col-6">
                                    <div className="d-flex justify-content-between align-items-center p-2 mb-2 rounded" style={{backgroundColor: "#f1f5f9", border: "1px solid #cbd5e1"}}>
                                      <span className="small fw-medium">{plan.planType}</span>
                                      <span className="small fw-bold" style={{color: plan.totalAmount > 0 ? '#059669' : '#6b7280'}}>{plan.displayAmount}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  )}
                </Row>
              </Col>
              <Col lg={4}>
                {/* Recent Scans */}
                <Card className="mb-4 chart-card-enhanced card-enhanced fade-in">
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-semibold text-gradient">
                      <MdSearch className="me-2" /> Recent Scans
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ height: "442px", overflowY: "auto" }}>
                    {filteredScans.length > 0 ? (
                      filteredScans.map((scan, index) => (
                        <div
                          key={scan._id}
                          className="mb-3"
                          style={{
                            backgroundColor: "#f8f9fa",
                            borderRadius: "4px",
                            border: "1px solid #e9ecef",
                            padding: "12px",
                            transition: "none",
                            boxShadow: "none",
                            animationDelay: `${index * 0.05}s`,
                          }}
                        >
                          <div className="d-flex align-items-start gap-3">
                            <div className="mt-1">
                              {scan.reportSend ? (
                                <MdCheckCircle
                                  className="text-success"
                                  size={20}
                                />
                              ) : (
                                <MdInfo className="text-muted" size={20} />
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <div className="fw-semibold">
                                    {scan.domain}
                                  </div>
                                  <div className="small text-muted">
                                    ID: {scan._id.slice(-8)}
                                  </div>
                                </div>
                                <Badge
                                  style={{
                                    backgroundColor: scan.overAllRiskScore
                                      ? scan.overAllRiskScore > 30
                                        ? "#ef4444"
                                        : scan.overAllRiskScore > 15
                                        ? "#f59e0b"
                                        : "#10b981"
                                      : "#ffc107",
                                    color: "white !important",
                                    border: "none !important",
                                    fontSize: "0.75rem",
                                    padding: "0.25rem 0.5rem",
                                    borderRadius: "0.375rem",
                                  }}
                                  className="small"
                                >
                                  {scan.overAllRiskScore
                                    ? `Risk: ${scan.overAllRiskScore.toFixed(
                                        1
                                      )}`
                                    : "Pending"}
                                </Badge>
                              </div>
                              <div className="small text-muted d-flex align-items-start justify-content-between">
                                <div className="fw-medium">
                                  <p className="mb-0">
                                    {scan.user.firstName} {scan.user.lastName}
                                  </p>
                                  <div className="d-flex align-items-center gap-1">
                                    <FaBuilding size={12} />{" "}
                                    {scan.oemPartner.companyName}
                                  </div>
                                </div>
                                <div>
                                  {new Date(
                                    scan.createdAt
                                  ).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-muted py-4">
                        <p className="mb-0">
                          {hasNoData
                            ? "No recent scans for selected OEM"
                            : "No recent scans available"}
                        </p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col lg={8}>
                {/* Top Revenue OEM Partners */}
                <Card
                  className="mb-4 chart-card-enhanced card-enhanced fade-in"
                  style={{ animationDelay: "0.2s" }}
                >
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-semibold text-gradient">
                      <MdTrendingUp className="me-2" /> Top Revenue OEM Partners
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ height: "350px", overflowY: "auto" }}>
                    {topRevenuePartners.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-borderless">
                          <thead>
                            <tr className="text-muted">
                              <th>S.No.</th>
                              <th>Partner Name</th>
                              <th>USD</th>
                              <th>JPY</th>
                              <th>HKD</th>
                              <th>SGD</th>
                              <th>AUD</th>
                              <th>Scans</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topRevenuePartners.map((partner, index) => (
                                <tr key={partner._id}>
                                  <td>
                                    <img
                                      src={partner.oemPartner.logo}
                                      alt={partner.oemPartner.companyName}
                                      style={{
                                        width: "40px",
                                        height: "40px",
                                        borderRadius: "6px",
                                        objectFit: "cover",
                                        backgroundColor: "#f3f4f6",
                                        padding: "4px"
                                      }}
                                    />
                                  </td>
                                  <td className="fw-medium">{partner.oemPartner.companyName}</td>
                                  <td className="fw-bold text-success">${partner.revenue.usd || 0}</td>
                                  <td className="fw-bold text-info">¥{partner.revenue.jpy || 0}</td>
                                  <td className="fw-bold text-warning">HK${partner.revenue.hkd || 0}</td>
                                  <td className="fw-bold text-danger">S${partner.revenue.sgd || 0}</td>
                                  <td className="fw-bold text-secondary">A${partner.revenue.aud || 0}</td>
                                  <td className="fw-bold text-primary">{partner.scanCount}</td>
                                </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <p className="mb-0">
                          {hasNoData
                            ? "No revenue data for selected OEM"
                            : "No revenue partners data available"}
                        </p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                <Row>
                  <Col lg={12}>
                    {/* Conversion Metrics */}
                    <Card
                      className="mb-4 chart-card-enhanced card-enhanced fade-in"
                      style={{ animationDelay: "0.1s" }}
                    >
                      <Card.Header className="chart-header-enhanced">
                        <h6 className="mb-0 fw-semibold text-gradient">
                          <MdInfo className="me-2" /> Conversion Metrics
                        </h6>
                      </Card.Header>
                      <Card.Body className="py-0 px-1">
                        {rates ? (
                          <Chart
                            options={{
                              chart: {
                                type: "line",
                                height: 150,
                                toolbar: { show: false },
                              },
                              colors: ["#06b6d4", "#8b5cf6"],
                              stroke: {
                                curve: "smooth",
                                width: 2,
                              },
                              xaxis: {
                                categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                                labels: {
                                  style: {
                                    fontSize: "10px",
                                  },
                                },
                              },
                              yaxis: {
                                labels: {
                                  style: {
                                    fontSize: "10px",
                                  },
                                },
                              },
                              legend: {
                                show: true,
                                position: "bottom",
                                fontSize: "10px",
                              },
                              grid: {
                                borderColor: "#f1f5f9",
                              },
                              dataLabels: {
                                enabled: false,
                              },
                            }}
                            series={[
                              {
                                name: "Verification",
                                data: [85, 86, 87, 87, 87, 87],
                              },
                              {
                                name: "Payment",
                                data: [65, 66, 67, 68, 68, 68],
                              },
                            ]}
                            type="line"
                            height={150}
                          />
                        ) : (
                          <div className="text-center text-muted py-3">
                            <p className="mb-0 small">
                              {hasNoData
                                ? "No conversion metrics for selected OEM"
                                : "No conversion metrics available"}
                            </p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col lg={12}>
                    {/* Top Domains */}
                    <Card
                      className="mb-4 chart-card-enhanced card-enhanced fade-in"
                      style={{ animationDelay: "0.2s" }}
                    >
                      <Card.Header className="chart-header-enhanced">
                        <h6 className="mb-0 fw-semibold text-gradient">
                          <MdBusiness className="me-2" /> Top Domains
                        </h6>
                      </Card.Header>
                      <Card.Body
                        className="py-0 px-1"
                        style={{ height: "110px", overflowY: "auto" }}
                      >
                        {topDomains.length > 0 ? (
                          <div className="py-2">
                            {topDomains.map((domain, index) => (
                              <div
                                key={index}
                                className="d-flex justify-content-between align-items-center mb-2"
                                style={{
                                  padding: "8px 12px",
                                  backgroundColor: "#f8f9fa",
                                  borderRadius: "4px",
                                  border: "1px solid #e9ecef",
                                }}
                              >
                                <div className="d-flex align-items-center">
                                  <div
                                    className="me-2"
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      backgroundColor: index === 0 ? "#06b6d4" : "#8b5cf6",
                                      borderRadius: "50%",
                                    }}
                                  ></div>
                                  <span className="small fw-medium">{domain.domain}</span>
                                </div>
                                <span className="small fw-bold text-primary">
                                  {domain.scanCount}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center text-muted py-3">
                            <p className="mb-0 small">
                              {hasNoData
                                ? "No domain data for selected OEM"
                                : "No domain data available"}
                            </p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* Monthly Growth Trends */}
            <Row>
              <Col lg={8}>
                <Card
                  className="chart-card-enhanced card-enhanced fade-in"
                  style={{ animationDelay: "0.4s" }}
                >
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-semibold text-gradient">
                      <MdTrendingUp className="me-2" /> Monthly Growth Trends
                    </h6>
                  </Card.Header>
                  <Card.Body className="" style={{height:"400px"}}>
                    <MonthlyGrowthChart
                      data={dashboardData?.response?.monthlyGrowthTrends}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={4}>
                {/* Recent Transactions */}
                <Card
                  className="mb-4 chart-card-enhanced card-enhanced fade-in"
                  style={{ animationDelay: "0.3s" }}
                >
                  <Card.Header className="chart-header-enhanced">
                    <h6 className="mb-0 fw-semibold text-gradient">
                      <MdAttachMoney className="me-2" /> Recent Transactions
                    </h6>
                  </Card.Header>
                  <Card.Body style={{ height: "400px", overflowY: "auto" }}>
                    {recentTransactions.length > 0 ? (
                      recentTransactions.map((transaction, index) => {
                        const colors = ["#8b5cf6", "#06b6d4", "#10b981", "#f59e0b", "#ef4444"];
                        const color = colors[index % colors.length];
                        const timeAgo = new Date(transaction.createdAt) ? 
                          Math.floor((new Date() - new Date(transaction.createdAt)) / (1000 * 60 * 60)) + " hrs" : "Today";
                        
                        return (
                          <div
                            key={transaction._id}
                            className="d-flex align-items-start mb-3"
                            style={{ animationDelay: `${index * 0.05}s` }}
                          >
                            <div
                              className="rounded-circle me-3 mt-1"
                              style={{
                                width: "8px",
                                height: "8px",
                                backgroundColor: color,
                                flexShrink: 0
                              }}
                            ></div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <span className="fw-medium small">
                                    Payment of {transaction.currency.toUpperCase()} {transaction.amount} by{" "}
                                    <span style={{ color: color }}>
                                      {transaction.user.firstName.charAt(0).toUpperCase() + transaction.user.firstName.slice(1)}{" "}
                                      {transaction.user.lastName.charAt(0).toUpperCase() + transaction.user.lastName.slice(1)}
                                    </span>{" "}
                                    {transaction.status === "succeeded" ? "completed" : "failed"}
                                  </span>
                                  <div className="small text-muted mt-1">
                                    {transaction.oemPartner.companyName}
                                  </div>
                                </div>
                                <span className="small text-muted">
                                  {timeAgo}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center text-muted py-3">
                        <p className="mb-0">
                          {hasNoData
                            ? "No recent transactions for selected OEM"
                            : "No recent transactions available"}
                        </p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </div>
  );
};

// Monthly Growth Chart Component
const MonthlyGrowthChart = ({ data }) => {
  if (
    !data ||
    !data.users ||
    !Array.isArray(data.users) ||
    data.users.length === 0
  ) {
    return (
      <div className="text-center text-muted py-4">
        <p className="mb-0">No monthly growth data available</p>
      </div>
    );
  }

  const getMonthName = (monthId) => {
    if (!monthId) return "";
    const [year, month] = monthId.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleDateString("en-US", { month: "short" });
  };

  const chartData = {
    users: Array.isArray(data.users) ? data.users : [],
    scans: Array.isArray(data.scans) ? data.scans : [],
    revenue: Array.isArray(data.revenue) ? data.revenue : [],
  };

  const categories = chartData.users.map((item) => getMonthName(item?._id));

  return (
    <Chart
      options={{
        chart: {
          type: "line",
          height: 350,
          toolbar: { show: false },
        },
        colors: [
          "#0d6efd",
          "#198754",
          "#ffc107",
          "#dc3545",
          "#6f42c1",
          "#fd7e14",
          "#20c997",
        ],
        stroke: {
          curve: "smooth",
          width: 3,
        },
        xaxis: {
          categories: categories,
        },
        yaxis: [
          {
            title: { text: "Revenue (Multi-Currency)" },
            labels: { style: { colors: "#ffc107" } },
          },
          {
            opposite: true,
            title: { text: "Users & Scans" },
            labels: { style: { colors: "#0d6efd" } },
          },
        ],
        legend: {
          position: "top",
          horizontalAlign: "right",
          offsetY: -5,
          itemMargin: {
            horizontal: 10,
            vertical: 3,
          },
        },
        grid: {
          borderColor: "#f1f1f1",
        },
      }}
      series={[
        {
          name: "Revenue (AUD)",
          data: chartData.revenue.map((item) => item?.aud || 0),
          yAxisIndex: 0,
        },
        {
          name: "Revenue (USD)",
          data: chartData.revenue.map((item) => item?.usd || 0),
          yAxisIndex: 0,
        },
        {
          name: "Revenue (HKD)",
          data: chartData.revenue.map((item) => item?.hkd || 0),
          yAxisIndex: 0,
        },
        {
          name: "Revenue (SGD)",
          data: chartData.revenue.map((item) => item?.sgd || 0),
          yAxisIndex: 0,
        },
        {
          name: "Revenue (JPY)",
          data: chartData.revenue.map((item) => item?.jpy || 0),
          yAxisIndex: 0,
        },
        {
          name: "Scans",
          data: chartData.scans.map((item) => item?.count || 0),
          yAxisIndex: 1,
        },
        {
          name: "Users",
          data: chartData.users.map((item) => item?.count || 0),
          yAxisIndex: 1,
        },
      ]}
      type="line"
      height={350}
    />
  );
};

export default Dashboard;
